import { useCallback, useEffect, useMemo, useState, type ComponentType, type ReactNode, type SVGProps } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import {
  ArrowPathIcon,
  BellAlertIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
  PaperAirplaneIcon,
  TruckIcon,
} from "@heroicons/react/24/outline";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { formatCurrency } from "@/lib/documentCalc";
import {
  activateHeroDokument,
  dokumentDisplayNumber,
  getDokumentTypLabel,
  isHeroCustomerDocumentListItem,
  listDokumenteWithTotal,
  type DokumentListItem,
} from "@/lib/dokumente";
import { formatDateTime, formatRelative } from "@/lib/format";
import {
  fetchInvoiceRevenueReport,
  formatEuroCompact,
  formatEuroDe,
  OPEN_INVOICE_BUCKET_LABELS,
  OPEN_INVOICE_BUCKETS,
  type InvoiceRevenueReport,
} from "@/lib/heroDocumentReports";
import { listLieferscheineSummary, type LieferscheinSummary } from "@/lib/lieferscheine";
import { formatMahnungDate, loadMahnlauf, type MahnlaufItem } from "@/lib/mahnwesen";
import { fetchOffenePosten, type OffenePostenReport } from "@/lib/offenePosten";
import { listTimesheetsSummary, type TimesheetSummary } from "@/lib/timesheets";
import { toast } from "@/lib/toast";
import { useRealtimeSync } from "@/lib/useRealtime";
import { filterNewBillingLieferscheine, splitBillingHomeTimesheets } from "@/pages/home/homeRole";
import { useDocumentCreate } from "@/pages/dokumente/useDocumentCreate";
import { useDocumentEditor } from "@/pages/dokumente/useDocumentEditor";
import {
  buildBuchhaltungTasks,
  findMonth,
  formatMonthLabel,
  lageLabel,
  lageTone,
  monthKey,
  overdueShare,
  sortMahnbar,
  totalOpenTasks,
  type BuchhaltungTask,
  type BuchhaltungTaskTone,
  type LageTone,
} from "@/pages/dokumente/buchhaltungUebersicht";
import "@/pages/auswertungen/auswertungen.css";

const LIST_LIMIT = 8;

interface OverviewData {
  offenePosten: OffenePostenReport | null;
  mahnbar: MahnlaufItem[];
  entwuerfe: { items: DokumentListItem[]; total: number };
  zuVersenden: { items: DokumentListItem[]; total: number };
  signedTimesheets: TimesheetSummary[];
  questionTimesheets: TimesheetSummary[];
  neueLieferscheine: LieferscheinSummary[];
  revenue: InvoiceRevenueReport | null;
  partialErrors: string[];
}

function formatBelegdatum(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value.length === 10 ? `${value}T12:00:00` : value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("de-DE");
}

const TASK_ICONS: Record<string, ComponentType<SVGProps<SVGSVGElement>>> = {
  mahnungen: BellAlertIcon,
  versand: PaperAirplaneIcon,
  nachweise: ClipboardDocumentCheckIcon,
  lieferscheine: TruckIcon,
  rueckfragen: ChatBubbleLeftRightIcon,
  entwuerfe: DocumentTextIcon,
};

const TONE_CLASSES: Record<BuchhaltungTaskTone, { icon: string; count: string }> = {
  danger: { icon: "bg-status-red/12 text-status-red", count: "text-status-red" },
  warning: { icon: "bg-status-orange/12 text-status-orange", count: "text-status-orange" },
  info: { icon: "bg-accent-soft text-accent", count: "text-label" },
  neutral: { icon: "bg-fill-tertiary text-label-secondary", count: "text-label" },
};

const LAGE_CLASSES: Record<LageTone, { badge: string; bar: string }> = {
  good: { badge: "bg-status-green/12 text-status-green", bar: "bg-status-green" },
  attention: { badge: "bg-status-orange/12 text-status-orange", bar: "bg-status-orange" },
  critical: { badge: "bg-status-red/12 text-status-red", bar: "bg-status-red" },
};

export function BuchhaltungUebersichtPage() {
  const { open: openDocumentCreate } = useDocumentCreate();
  const { open: openDocumentEditor, isOpen: documentEditorOpen } = useDocumentEditor();
  const [data, setData] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [heroOpenBusyId, setHeroOpenBusyId] = useState<number | null>(null);
  const [loadedAt, setLoadedAt] = useState<Date | null>(null);

  const reload = useCallback(async (options?: { silent?: boolean }) => {
    if (!options?.silent) setLoading(true);
    setError(null);
    const year = new Date().getFullYear();
    const [op, mahn, entw, vers, ts, ls, rev] = await Promise.allSettled([
      fetchOffenePosten(),
      loadMahnlauf({ filter: "mahnbar" }),
      listDokumenteWithTotal({
        typ: "rechnung",
        status: "entwurf",
        limit: LIST_LIMIT,
        sort: "created_at",
        direction: "desc",
      }),
      listDokumenteWithTotal({
        typ: "rechnung",
        status: "erstellt",
        limit: LIST_LIMIT,
        sort: "belegdatum",
        direction: "desc",
      }),
      listTimesheetsSummary(),
      listLieferscheineSummary(),
      fetchInvoiceRevenueReport(year),
    ]);

    const partialErrors: string[] = [];
    const fail = (label: string, reason: unknown) => {
      partialErrors.push(
        `${label}: ${reason instanceof ApiError || reason instanceof Error ? reason.message : "nicht verfügbar"}`,
      );
    };

    if (op.status === "rejected") fail("Offene Posten", op.reason);
    if (mahn.status === "rejected") fail("Mahnwesen", mahn.reason);
    if (entw.status === "rejected") fail("Rechnungsentwürfe", entw.reason);
    if (vers.status === "rejected") fail("Versand", vers.reason);
    if (ts.status === "rejected") fail("Stundennachweise", ts.reason);
    if (ls.status === "rejected") fail("Lieferscheine", ls.reason);
    if (rev.status === "rejected") fail("Umsatz", rev.reason);

    const allFailed = [op, mahn, entw, vers, ts, ls, rev].every((r) => r.status === "rejected");
    if (allFailed) {
      setError("Die Übersicht konnte nicht geladen werden.");
      if (!options?.silent) setData(null);
      setLoading(false);
      return;
    }

    const split =
      ts.status === "fulfilled"
        ? splitBillingHomeTimesheets(ts.value)
        : { signed: [], drafts: [], questions: [] };

    setData({
      offenePosten: op.status === "fulfilled" ? op.value : null,
      mahnbar: mahn.status === "fulfilled" ? sortMahnbar(mahn.value.items ?? []) : [],
      entwuerfe:
        entw.status === "fulfilled"
          ? { items: entw.value.items, total: entw.value.totalCount ?? entw.value.items.length }
          : { items: [], total: 0 },
      zuVersenden:
        vers.status === "fulfilled"
          ? { items: vers.value.items, total: vers.value.totalCount ?? vers.value.items.length }
          : { items: [], total: 0 },
      signedTimesheets: split.signed,
      questionTimesheets: split.questions,
      neueLieferscheine: ls.status === "fulfilled" ? filterNewBillingLieferscheine(ls.value) : [],
      revenue: rev.status === "fulfilled" ? rev.value : null,
      partialErrors,
    });
    setLoadedAt(new Date());
    setLoading(false);
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  useRealtimeSync({
    domains: ["dokumente", "hero", "timesheets"],
    extraScopes: ["dokumente:list", "hero:list", "mahnwesen:list", "timesheets:list"],
    extraEvents: ["dokument_status_changed", "hero_changed", "timesheets_changed"],
    debounceMs: 800,
    paused: documentEditorOpen,
    onChange: () => void reload({ silent: true }),
  });

  const openInvoice = useCallback(
    async (row: DokumentListItem) => {
      if (isHeroCustomerDocumentListItem(row)) {
        const heroId = row.hero_document_id;
        if (!heroId || heroId <= 0 || heroOpenBusyId != null) return;
        setHeroOpenBusyId(heroId);
        try {
          const activated = await activateHeroDokument(heroId);
          openDocumentEditor({ docId: activated.id, onClosed: () => void reload({ silent: true }) });
          void reload({ silent: true });
        } catch (err) {
          toast.error(
            err instanceof ApiError ? err.message : "Hero-Dokument konnte nicht geöffnet werden.",
          );
        } finally {
          setHeroOpenBusyId(null);
        }
        return;
      }
      openDocumentEditor({ docId: row.id, onClosed: () => void reload({ silent: true }) });
    },
    [heroOpenBusyId, openDocumentEditor, reload],
  );

  const openById = useCallback(
    (id: number) => {
      openDocumentEditor({ docId: id, onClosed: () => void reload({ silent: true }) });
    },
    [openDocumentEditor, reload],
  );

  const tasks = useMemo<BuchhaltungTask[]>(
    () =>
      buildBuchhaltungTasks({
        mahnungen: data?.mahnbar.length ?? 0,
        zuVersenden: data?.zuVersenden.total ?? 0,
        entwuerfe: data?.entwuerfe.total ?? 0,
        nachweiseAbzurechnen: data?.signedTimesheets.length ?? 0,
        neueLieferscheine: data?.neueLieferscheine.length ?? 0,
        rueckfragen: data?.questionTimesheets.length ?? 0,
      }),
    [data],
  );
  const openTaskCount = totalOpenTasks(tasks);

  const summary = data?.offenePosten?.summary ?? null;
  const tone = lageTone(summary);
  const share = overdueShare(summary);

  const topCustomers = useMemo(
    () =>
      [...(data?.offenePosten?.by_customer ?? [])]
        .filter((row) => row.open_amount > 0)
        .sort((a, b) => b.open_amount - a.open_amount)
        .slice(0, 6),
    [data?.offenePosten?.by_customer],
  );

  const now = new Date();
  const thisMonth = findMonth(data?.revenue?.by_month, monthKey(now));
  const revenueSummary = data?.revenue?.summary ?? null;

  return (
    <AppShell
      title="Buchhaltung"
      subtitle="Was heute offen ist und wie die Zahlungslage aussieht – auf einen Blick."
      actions={
        <>
          <Button
            type="button"
            variant="secondary"
            onClick={() => void reload()}
            loading={loading && data != null}
            leftIcon={<ArrowPathIcon className="h-4 w-4" aria-hidden />}
          >
            Aktualisieren
          </Button>
          <Button type="button" data-tour="dokument-neu" onClick={() => openDocumentCreate({ typ: "rechnung" })}>
            Neue Rechnung
          </Button>
        </>
      }
    >
      {error ? <Alert variant="error">{error}</Alert> : null}
      {data?.partialErrors.length ? (
        <Alert variant="warning" title="Teilweise nicht geladen">
          {data.partialErrors.join(" · ")}
        </Alert>
      ) : null}

      {loading && !data ? (
        <div className="flex items-center justify-center py-16 text-label-secondary">
          <Spinner />
          <span className="ml-2 text-sm">Lädt Übersicht …</span>
        </div>
      ) : null}

      {data ? (
        <div className="space-y-5">
          {/* ─── Lage ─────────────────────────────────────────────────────── */}
          <section className="overflow-hidden rounded-ios-lg bg-card shadow-ios-md ring-1 ring-separator/35 dark:ring-separator/50">
            <div className="grid gap-0 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] lg:divide-x lg:divide-separator/70">
              <div className="px-5 py-5 md:px-6">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-label-tertiary">
                    Offene Forderungen
                  </span>
                  <span
                    className={clsx(
                      "inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold",
                      LAGE_CLASSES[tone].badge,
                    )}
                  >
                    {tone === "good" ? <CheckCircleIcon className="h-3.5 w-3.5" aria-hidden /> : null}
                    {lageLabel(tone)}
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="text-3xl font-semibold tabular-nums tracking-tight text-label md:text-4xl">
                    {summary ? formatEuroDe(summary.open_amount) : "—"}
                  </span>
                  <span className="text-sm text-label-secondary">
                    {summary ? `${summary.open_count} offene Rechnungen` : "keine Daten"}
                  </span>
                </div>

                {summary && summary.open_amount > 0 ? (
                  <>
                    <div
                      className="mt-4 flex h-2.5 w-full overflow-hidden rounded-full bg-fill-tertiary"
                      role="img"
                      aria-label={`${Math.round(share * 100)} % überfällig`}
                    >
                      <div
                        className={clsx("h-full rounded-full transition-all", LAGE_CLASSES[tone].bar)}
                        style={{ width: `${Math.max(2, share * 100)}%` }}
                      />
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
                      <MiniStat
                        label="Überfällig"
                        value={formatEuroCompact(summary.overdue_amount)}
                        hint={`${summary.overdue_count} Rechnungen · ${Math.round(share * 100)} %`}
                        tone={summary.overdue_amount > 0 ? tone : undefined}
                      />
                      <MiniStat
                        label="Noch nicht fällig"
                        value={formatEuroCompact(summary.not_due_amount)}
                        hint={`${summary.not_due_count} Rechnungen`}
                      />
                      <MiniStat
                        label="Über 60 Tage"
                        value={formatEuroCompact(summary.buckets?.d61_plus?.amount ?? 0)}
                        hint={`${summary.buckets?.d61_plus?.count ?? 0} Rechnungen`}
                        tone={(summary.buckets?.d61_plus?.amount ?? 0) > 0 ? "critical" : undefined}
                      />
                    </div>
                  </>
                ) : (
                  <p className="mt-3 text-sm text-label-secondary">
                    Aktuell sind keine Rechnungen offen.
                  </p>
                )}
                <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-label-tertiary">
                  <Link to="/dokumente/offene-posten" className="font-medium text-accent hover:underline">
                    Offene Posten im Detail
                  </Link>
                  {data.offenePosten?.last_synced_at ? (
                    <span>Hero-Sync {formatRelative(data.offenePosten.last_synced_at) || formatDateTime(data.offenePosten.last_synced_at)}</span>
                  ) : null}
                  {loadedAt ? <span>Stand {loadedAt.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })} Uhr</span> : null}
                </div>
              </div>

              <div className="border-t border-separator/70 px-5 py-5 md:px-6 lg:border-t-0">
                <span className="text-xs font-semibold uppercase tracking-wide text-label-tertiary">
                  Umsatz & Zahlungen
                </span>
                <dl className="mt-3 space-y-3">
                  <KeyValue
                    label={formatMonthLabel(now)}
                    value={thisMonth ? formatEuroCompact(thisMonth.total_amount) : "—"}
                    hint={thisMonth ? `${thisMonth.invoice_count} Rechnungen · ${formatEuroCompact(thisMonth.paid_amount)} bezahlt` : "keine Rechnungen"}
                  />
                  <KeyValue
                    label={`Jahr ${now.getFullYear()}`}
                    value={revenueSummary ? formatEuroCompact(revenueSummary.total_amount) : "—"}
                    hint={
                      revenueSummary
                        ? `${formatEuroCompact(revenueSummary.paid_amount)} bezahlt · ${formatEuroCompact(revenueSummary.open_amount)} offen`
                        : undefined
                    }
                  />
                  <KeyValue
                    label="Ø Zahlungsdauer"
                    value={
                      revenueSummary?.avg_payment_days != null
                        ? `${Math.round(revenueSummary.avg_payment_days)} Tage`
                        : "—"
                    }
                    hint={
                      revenueSummary?.median_payment_days != null
                        ? `Median ${Math.round(revenueSummary.median_payment_days)} Tage`
                        : undefined
                    }
                  />
                </dl>
                <Link
                  to="/auswertungen"
                  className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline"
                >
                  Zu den Auswertungen
                  <ChevronRightIcon className="h-3.5 w-3.5" aria-hidden />
                </Link>
              </div>
            </div>
          </section>

          {/* ─── Aufgaben ─────────────────────────────────────────────────── */}
          <section>
            <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2 px-1">
              <h2 className="text-base font-semibold text-label">Heute zu erledigen</h2>
              <span className="text-sm text-label-secondary">
                {openTaskCount === 0
                  ? "Nichts offen – alles erledigt."
                  : `${openTaskCount.toLocaleString("de-DE")} offene ${openTaskCount === 1 ? "Aufgabe" : "Aufgaben"}`}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
              {tasks.map((task) => (
                <TaskTile key={task.key} task={task} />
              ))}
            </div>
          </section>

          {/* ─── Listen ───────────────────────────────────────────────────── */}
          <div className="grid gap-4 xl:grid-cols-3">
            <div className="space-y-4 xl:col-span-2">
              <Card
                title={<CountTitle title="Jetzt mahnen" count={data.mahnbar.length} tone="danger" />}
                description="Überfällige Rechnungen, für die die nächste Mahnstufe ansteht – älteste zuerst."
                actions={<SectionLink href="/dokumente/mahnwesen" label="Zum Mahnwesen" />}
                bodyClassName="!p-0"
              >
                {data.mahnbar.length === 0 ? (
                  <DoneHint>Keine Mahnungen fällig.</DoneHint>
                ) : (
                  <div className="eh-home-table-scroll">
                    <table className="eh-home-table eh-home-table--responsive w-full text-sm">
                      <thead>
                        <tr>
                          <th>Kunde</th>
                          <th>Rechnung</th>
                          <th>Überfällig</th>
                          <th className="text-right">Offen</th>
                          <th>Nächste Stufe</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.mahnbar.slice(0, LIST_LIMIT).map((row) => (
                          <tr key={row.dokument_id}>
                            <td className="max-w-[16rem] truncate font-medium text-label" title={row.kunde_name || undefined}>
                              {row.kunde_name || "—"}
                            </td>
                            <td>
                              <button
                                type="button"
                                className="font-medium text-accent hover:underline"
                                onClick={() => openById(row.dokument_id)}
                              >
                                {row.dokument_nummer || `#${row.dokument_id}`}
                              </button>
                            </td>
                            <td>
                              <span
                                className={clsx(
                                  "font-medium tabular-nums",
                                  row.tage_ueberfaellig > 60
                                    ? "text-status-red"
                                    : row.tage_ueberfaellig > 30
                                      ? "text-status-orange"
                                      : "text-label",
                                )}
                              >
                                {row.tage_ueberfaellig} Tage
                              </span>
                              <div className="text-xs text-label-tertiary">
                                fällig {formatMahnungDate(row.faelligkeitsdatum)}
                              </div>
                            </td>
                            <td className="text-right font-semibold tabular-nums text-label">
                              {formatCurrency(row.summe_offen, "EUR")}
                            </td>
                            <td className="text-xs">
                              <div className="font-medium text-label">{row.naechste_stufe_label || "—"}</div>
                              {row.aktuelle_stufe_label ? (
                                <div className="text-label-tertiary">bisher {row.aktuelle_stufe_label}</div>
                              ) : null}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <MoreHint shown={Math.min(LIST_LIMIT, data.mahnbar.length)} total={data.mahnbar.length} />
              </Card>

              <Card
                title={<CountTitle title="Zu versenden" count={data.zuVersenden.total} tone="warning" />}
                description="Erstellte Rechnungen, die noch nicht an den Kunden gegangen sind."
                actions={<SectionLink href="/dokumente/rechnungen?status=erstellt" label="Alle anzeigen" />}
                bodyClassName="!p-0"
              >
                <InvoiceList items={data.zuVersenden.items} emptyText="Alle erstellten Rechnungen sind versendet." onOpen={(row) => void openInvoice(row)} />
                <MoreHint shown={data.zuVersenden.items.length} total={data.zuVersenden.total} />
              </Card>

              <Card
                title={<CountTitle title="Rechnungsentwürfe" count={data.entwuerfe.total} tone="neutral" />}
                description="Entwürfe fertigstellen und als Rechnung erstellen."
                actions={<SectionLink href="/dokumente/rechnungen?status=entwurf" label="Alle anzeigen" />}
                bodyClassName="!p-0"
              >
                <InvoiceList items={data.entwuerfe.items} emptyText="Keine Rechnungen im Entwurf." onOpen={(row) => void openInvoice(row)} />
                <MoreHint shown={data.entwuerfe.items.length} total={data.entwuerfe.total} />
              </Card>
            </div>

            <div className="space-y-4">
              <Card title="Altersstruktur" description="Offene Beträge nach Fälligkeit.">
                {summary && summary.open_amount > 0 ? (
                  <div className="space-y-3">
                    <div className="flex h-3 w-full overflow-hidden rounded-full ring-1 ring-separator/40" role="img" aria-label="Offene Beträge nach Fälligkeit">
                      {OPEN_INVOICE_BUCKETS.map((bucket) => {
                        const amount = summary.buckets[bucket]?.amount ?? 0;
                        if (amount <= 0) return null;
                        return (
                          <div
                            key={bucket}
                            className={clsx("eh-docreport-aging__segment", `eh-docreport-aging__segment--${bucket}`)}
                            style={{ width: `${(amount / summary.open_amount) * 100}%` }}
                            title={`${OPEN_INVOICE_BUCKET_LABELS[bucket]}: ${formatEuroDe(amount)}`}
                          />
                        );
                      })}
                    </div>
                    <ul className="divide-y divide-separator/60">
                      {OPEN_INVOICE_BUCKETS.map((bucket) => {
                        const entry = summary.buckets[bucket] ?? { count: 0, amount: 0 };
                        return (
                          <li key={bucket} className="flex items-center gap-2 py-2 text-sm">
                            <span className={clsx("inline-block h-2.5 w-2.5 shrink-0 rounded-sm eh-docreport-aging__segment", `eh-docreport-aging__segment--${bucket}`)} aria-hidden />
                            <span className="min-w-0 flex-1 truncate text-label-secondary">{OPEN_INVOICE_BUCKET_LABELS[bucket]}</span>
                            <span className="text-xs tabular-nums text-label-tertiary">{entry.count}</span>
                            <span className="w-24 text-right font-medium tabular-nums text-label">{formatEuroCompact(entry.amount)}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ) : (
                  <p className="text-sm text-label-secondary">Keine offenen Rechnungen.</p>
                )}
              </Card>

              <Card
                title="Größte offene Kunden"
                description="Nach offenem Betrag."
                actions={<SectionLink href="/dokumente/offene-posten" label="Alle" />}
              >
                {topCustomers.length ? (
                  <ul className="divide-y divide-separator/60">
                    {topCustomers.map((row) => (
                        <li key={`${row.kunden_id ?? row.kunde}`} className="flex items-center gap-3 py-2">
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-medium text-label" title={row.kunde}>{row.kunde}</div>
                            <div className="text-xs text-label-tertiary">
                              {row.open_count} {row.open_count === 1 ? "Rechnung" : "Rechnungen"}
                              {row.max_days_overdue > 0 ? ` · bis ${row.max_days_overdue} Tage überfällig` : ""}
                            </div>
                          </div>
                          <span className={clsx("shrink-0 text-sm font-semibold tabular-nums", row.max_days_overdue > 30 ? "text-status-orange" : "text-label")}>
                            {formatEuroCompact(row.open_amount)}
                          </span>
                        </li>
                      ))}
                  </ul>
                ) : (
                  <p className="text-sm text-label-secondary">Keine offenen Kunden.</p>
                )}
              </Card>

              <Card
                title={<CountTitle title="Nachweise abrechnen" count={data.signedTimesheets.length} tone="warning" />}
                description="Unterschriebene Stundennachweise ohne Rechnung."
                actions={<SectionLink href="/dokumente/stundennachweise?status=signed" label="Alle" />}
              >
                {data.signedTimesheets.length === 0 ? (
                  <DoneHint compact>Alle unterschriebenen Nachweise sind abgerechnet.</DoneHint>
                ) : (
                  <ul className="divide-y divide-separator/60">
                    {data.signedTimesheets.slice(0, 6).map((ts) => (
                      <li key={ts.id} className="py-2">
                        <Link to={`/dokumente/stundennachweise?q=${encodeURIComponent(ts.display_number || String(ts.timesheet_number || ts.id))}`} className="block">
                          <div className="flex items-center justify-between gap-2">
                            <span className="truncate text-sm font-medium text-accent hover:underline">
                              {ts.display_number || `#${ts.timesheet_number ?? ts.id}`}
                            </span>
                            {ts.total_hours != null ? (
                              <span className="shrink-0 text-xs tabular-nums text-label-secondary">
                                {Number(ts.total_hours).toLocaleString("de-DE", { maximumFractionDigits: 1 })} h
                              </span>
                            ) : null}
                          </div>
                          <div className="truncate text-xs text-label-tertiary">
                            {[ts.kunde || ts.hero_kunde, ts.auftragsnummer].filter(Boolean).join(" · ") || "—"}
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </div>
          </div>
        </div>
      ) : null}
    </AppShell>
  );
}

// ─── Bausteine ────────────────────────────────────────────────────────────────

function TaskTile({ task }: { task: BuchhaltungTask }) {
  const Icon = TASK_ICONS[task.key] ?? DocumentTextIcon;
  const done = task.count === 0;
  const tone = TONE_CLASSES[done ? "neutral" : task.tone];
  return (
    <Link
      to={task.href}
      className={clsx(
        "group flex min-h-[7.5rem] flex-col justify-between rounded-ios-lg bg-card p-4 shadow-ios ring-1 ring-separator/35 transition hover:-translate-y-0.5 hover:shadow-ios-md focus:outline-none focus-visible:ring-2 focus-visible:ring-accent dark:ring-separator/50",
        done && "opacity-80",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <span className={clsx("flex h-9 w-9 shrink-0 items-center justify-center rounded-xl", tone.icon)}>
          <Icon className="h-[18px] w-[18px]" aria-hidden />
        </span>
        {done ? (
          <CheckCircleIcon className="h-5 w-5 text-status-green" aria-hidden />
        ) : (
          <ChevronRightIcon className="h-4 w-4 text-label-quaternary transition group-hover:text-label-secondary" aria-hidden />
        )}
      </div>
      <div className="mt-3">
        <div className={clsx("text-2xl font-semibold tabular-nums leading-none", tone.count)}>
          {task.count.toLocaleString("de-DE")}
        </div>
        <div className="mt-1.5 text-sm font-medium leading-snug text-label">{task.label}</div>
        <div className="mt-0.5 line-clamp-2 text-xs leading-snug text-label-tertiary">
          {done ? "Erledigt" : task.hint}
        </div>
      </div>
    </Link>
  );
}

function MiniStat({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: LageTone;
}) {
  return (
    <div className="rounded-ios bg-fill-quaternary/50 px-3 py-2.5">
      <div className="text-[11px] font-medium uppercase tracking-wide text-label-tertiary">{label}</div>
      <div
        className={clsx(
          "mt-0.5 text-lg font-semibold tabular-nums",
          tone === "critical" ? "text-status-red" : tone === "attention" ? "text-status-orange" : "text-label",
        )}
      >
        {value}
      </div>
      {hint ? <div className="text-xs text-label-tertiary">{hint}</div> : null}
    </div>
  );
}

function KeyValue({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="text-sm text-label-secondary">{label}</dt>
      <dd className="text-right">
        <div className="text-sm font-semibold tabular-nums text-label">{value}</div>
        {hint ? <div className="text-xs text-label-tertiary">{hint}</div> : null}
      </dd>
    </div>
  );
}

function CountTitle({ title, count, tone }: { title: string; count: number; tone: BuchhaltungTaskTone }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      {title}
      {count > 0 ? (
        <span
          className={clsx(
            "rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums",
            tone === "danger"
              ? "bg-status-red text-white"
              : tone === "warning"
                ? "bg-status-orange text-white"
                : "bg-fill-tertiary text-label-secondary",
          )}
        >
          {count.toLocaleString("de-DE")}
        </span>
      ) : null}
    </span>
  );
}

function SectionLink({ href, label }: { href: string; label: string }) {
  return (
    <Link to={href} className="inline-flex items-center gap-1 text-sm font-medium text-accent hover:underline">
      {label}
      <ChevronRightIcon className="h-3.5 w-3.5" aria-hidden />
    </Link>
  );
}

function DoneHint({ children, compact }: { children: ReactNode; compact?: boolean }) {
  return (
    <div className={clsx("flex items-center gap-2 text-sm text-label-secondary", compact ? "py-1" : "px-5 py-6")}>
      <CheckCircleIcon className="h-5 w-5 shrink-0 text-status-green" aria-hidden />
      <span>{children}</span>
    </div>
  );
}

function MoreHint({ shown, total }: { shown: number; total: number }) {
  if (shown <= 0 || total <= shown) return null;
  return (
    <p className="border-t border-separator px-5 py-2 text-xs text-label-tertiary">
      {total.toLocaleString("de-DE")} insgesamt – die {shown} wichtigsten werden angezeigt.
    </p>
  );
}

function InvoiceList({
  items,
  emptyText,
  onOpen,
}: {
  items: DokumentListItem[];
  emptyText: string;
  onOpen: (row: DokumentListItem) => void;
}) {
  if (!items.length) return <DoneHint>{emptyText}</DoneHint>;
  return (
    <div className="eh-home-table-scroll">
      <table className="eh-home-table eh-home-table--responsive w-full text-sm">
        <thead>
          <tr>
            <th>Rechnung</th>
            <th>Kunde</th>
            <th>Auftrag</th>
            <th>Datum</th>
            <th className="text-right">Brutto</th>
          </tr>
        </thead>
        <tbody>
          {items.map((row) => (
            <tr key={`${row.quelle || "gd"}-${row.id}-${row.hero_document_id || 0}`}>
              <td className="max-w-[14rem]">
                <button
                  type="button"
                  className="truncate font-medium text-accent hover:underline"
                  title={dokumentDisplayNumber(row)}
                  onClick={() => onOpen(row)}
                >
                  {dokumentDisplayNumber(row)}
                </button>
                <div className="text-xs text-label-tertiary">{getDokumentTypLabel(row)}</div>
              </td>
              <td className="max-w-[14rem] truncate" title={row.kunde_name || undefined}>
                {row.kunde_name || "—"}
              </td>
              <td className="max-w-[10rem] truncate" title={row.auftrag_nummer || undefined}>
                {row.auftrag_nummer || "—"}
              </td>
              <td className="tabular-nums">{formatBelegdatum(row.belegdatum)}</td>
              <td className="text-right font-medium tabular-nums">
                {formatCurrency(row.summe_brutto, row.waehrung || "EUR")}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}